import { supabase } from "@/integrations/supabase/client";

const SIGNED_URL_EXPIRY = 3600; // 1 hour

/**
 * Resolves a lesson-files storage reference to a usable URL.
 * Handles both legacy full URLs and new path-only references.
 */
export async function getSignedFileUrl(bucketOrPath: string, filePath?: string): Promise<string> {
  // Support both (bucket, path) and legacy (path) signatures
  let bucket = "lesson-files";
  let path: string;
  if (filePath !== undefined) {
    bucket = bucketOrPath;
    path = filePath;
  } else {
    path = bucketOrPath;
  }

  // Extract path from legacy full public URLs
  if (path.includes(`/${bucket}/`)) {
    const parts = path.split(`/${bucket}/`);
    path = decodeURIComponent(parts[parts.length - 1]);
  } else if (path.startsWith("http")) {
    return path;
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_URL_EXPIRY);

  if (error || !data?.signedUrl) {
    console.error("Signed URL error:", error);
    return path; // fallback
  }
  return data.signedUrl;
}

/**
 * Resolves multiple file URLs to signed URLs in parallel.
 */
export async function getSignedFileUrls(
  files: { id: string; file_url: string }[]
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    files.map(async (f) => {
      const url = await getSignedFileUrl(f.file_url);
      return [f.id, url] as const;
    })
  );
  return Object.fromEntries(entries);
}
